// Markets.js — Fixed sections only. Full file with all fixes applied.
// Key fixes:
// 1. SSE connection now passes token as query param (EventSource can't set headers)
// 2. BetPanel payout calculation uses correct LMSR-based math matching backend
// 3. Leaderboard Avatar uses username (backend no longer returns full_name)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';
import BottomNav from '../components/BottomNav';
import API from '../config';
import { Helmet } from 'react-helmet-async';

const OPTION_COLORS = ['#3772ff','#9b5de5','#f3a94a','#00c9b1','#ff6b9d'];

// ── News Panel ────────────────────────────────────────────────────────────────
const NEWS_API_TOKEN = 'AhdSdx3N0tJ8ZA8mby6BRKmF1R6cDdvW0Wq7lvFD'; 
const CURRENTS_API_KEY = 'f9q76psa_KRdJDfNgs93rdS7mPbNU1_hthqMj4tKvNLN0pAq';

const NewsPanel = ({ markets, navigate, T, isMobile }) => {
  const [articles, setArticles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [centerIdx, setCenterIdx] = useState(0);
  const [paused, setPaused]       = useState(false);
  const [imgErrors, setImgErrors] = useState({});
  const autoRef     = useRef(null);
const touchStartX = useRef(null);
const wheelDelta  = useRef(0);
const wheelReset  = useRef(null);
const stageRef    = useRef(null);

   useEffect(() => {
    const cached = sessionStorage.getItem('mino_news_cache');
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 15 * 60 * 1000) {
          setArticles(data);
          setLoading(false);
          return;
        }
      } catch (_) {}
    }
    fetchNews();
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

useEffect(() => {
    if (!articles.length || paused || collapsed) return;
    autoRef.current = setInterval(() => {
      setCenterIdx(i => (i + 1) % articles.length);
    }, 3500);
    return () => clearInterval(autoRef.current);
  }, [articles.length, paused, collapsed]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || !articles.length) return;
    const onWheel = e => {
      e.preventDefault();
      setPaused(true);
      clearTimeout(wheelReset.current);
      wheelDelta.current += e.deltaX !== 0 ? e.deltaX : e.deltaY;
      if (Math.abs(wheelDelta.current) > 60) {
        const dir = wheelDelta.current > 0 ? 1 : -1;
        setCenterIdx(i => (i + dir + articles.length) % articles.length);
        wheelDelta.current = 0;
      }
      wheelReset.current = setTimeout(() => {
        wheelDelta.current = 0;
        setPaused(false);
      }, 1500);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [articles.length]);

  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = e => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 40) {
      const dir = dx > 0 ? 1 : -1;
      setCenterIdx(i => (i + dir + articles.length) % articles.length);
    }
    touchStartX.current = null;
  };

  const fetchNews = async () => {
    try {
       const tnaPromise = Promise.allSettled(
      ['Kenya', 'Africa', 'Nairobi'].map(q =>
        axios.get(`https://api.thenewsapi.com/v1/news/all?api_token=${NEWS_API_TOKEN}&search=${encodeURIComponent(q)}&language=en&limit=3&sort=published_at`)
      )
    );
    const currentsPromise = Promise.allSettled([
      axios.get(`https://api.currentsapi.services/v1/search?keywords=Kenya&language=en&apiKey=${CURRENTS_API_KEY}`),
      axios.get(`https://api.currentsapi.services/v1/search?keywords=Nairobi&language=en&apiKey=${CURRENTS_API_KEY}`),
    ]);

    // Show TNA results immediately when ready
    tnaPromise.then(tnaResults => {
      const seen = new Set();
      const partial = [];
      tnaResults.filter(r => r.status === 'fulfilled').flatMap(r => r.value.data?.data || []).forEach(a => {
        if (!seen.has(a.uuid)) { seen.add(a.uuid); partial.push({ id:a.uuid, title:a.title, image:a.image_url, url:a.url, published:a.published_at, category:a.categories?.[0]||'general', source:a.source }); }
      });
      if (partial.length > 0) { setArticles(partial.slice(0,12)); setLoading(false); }
    });

    const [tnaResults, currentsResults] = await Promise.allSettled([
        Promise.allSettled(
          ['Kenya', 'Africa', 'Nairobi'].map(q =>
            axios.get(`https://api.thenewsapi.com/v1/news/all?api_token=${NEWS_API_TOKEN}&search=${encodeURIComponent(q)}&language=en&limit=3&sort=published_at`)
          )
        ),
        Promise.allSettled([
          axios.get(`https://api.currentsapi.services/v1/search?keywords=Kenya&language=en&apiKey=${CURRENTS_API_KEY}`),
          axios.get(`https://api.currentsapi.services/v1/search?keywords=Nairobi&language=en&apiKey=${CURRENTS_API_KEY}`),
        ])
      ]);

      const seen = new Set();
      const articles = [];

      if (tnaResults.status === 'fulfilled') {
        tnaResults.value
          .filter(r => r.status === 'fulfilled')
          .flatMap(r => r.value.data?.data || [])
          .forEach(a => {
            if (!seen.has(a.uuid)) {
              seen.add(a.uuid);
              articles.push({ id: a.uuid, title: a.title, image: a.image_url, url: a.url, published: a.published_at, category: a.categories?.[0] || 'general', source: a.source });
            }
          });
      }

      if (currentsResults.status === 'fulfilled') {
        currentsResults.value
          .filter(r => r.status === 'fulfilled')
          .flatMap(r => r.value.data?.news || [])
          .forEach(a => {
            if (!seen.has(a.id)) {
              seen.add(a.id);
              articles.push({ id: a.id, title: a.title, image: a.image !== 'None' ? a.image : null, url: a.url, published: a.published, category: a.category?.[0] || 'general', source: a.author });
            }
          });
      }

       articles.sort((a, b) => new Date(b.published) - new Date(a.published));
      const final = articles.slice(0, 12);
      setArticles(final);
      try { sessionStorage.setItem('mino_news_cache', JSON.stringify({ data: final, ts: Date.now() })); } catch (_) {}
    } catch (_) {}
    finally { setLoading(false); }
  };

  const findRelatedMarket = (article) => {
    if (!markets?.length) return null;
    const text = `${article.title} ${article.description || ''}`.toLowerCase();
    const keywords = ['arsenal','manchester city','premier league','epl','world cup','fifa','morocco','france','argentina','spain','ruto','kenya','finance bill','gachagua','nairobi','safaricom','mpesa','apple','iphone','burna','showspeed','ramaphosa','trump','gaza','ceasefire'];
    for (const kw of keywords) {
      if (text.includes(kw)) {
        const match = markets.find(m => m.title.toLowerCase().includes(kw) || m.description?.toLowerCase().includes(kw));
        if (match) return match;
      }
    }
    const articleWords = new Set(text.split(/\W+/).filter(w => w.length > 4));
    let best = null, bestScore = 0;
    for (const m of markets) {
      const score = m.title.toLowerCase().split(/\W+/).filter(w => articleWords.has(w)).length;
      if (score > bestScore) { bestScore = score; best = m; }
    }
    return bestScore >= 2 ? best : null;
  };

  const ago = ts => {
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const srcName = url => {
    try { return new URL(url).hostname.replace('www.', ''); }
    catch (_) { return 'News'; }
  };

  const CATEGORY_COLORS = {
    sports: '#16c784', politics: '#f3a94a', technology: '#3772ff',
    business: '#9b5de5', entertainment: '#ff6b9d', general: '#00c9b1',
  };

  const cardW = isMobile ? 300 : 480;
const cardH = isMobile ? 280 : 340;

  const getCardStyle = (idx) => {
  const total = articles.length;
  let offset = idx - centerIdx;
  if (offset > total / 2)  offset -= total;
  if (offset < -total / 2) offset += total;
  const abs = Math.abs(offset);
  if (abs > 3) return { display: 'none' };

  return {
    position: 'absolute',
    left: '50%',
    top: 0,
    width: cardW,
  
    transform: `translateX(calc(-50% + ${offset * (isMobile ? 180 : 280)}px)) translateZ(${-abs * 60}px) rotateY(${offset * 14}deg) scale(${1 - abs * 0.07})`,
    opacity: 1 - abs * 0.18,
    zIndex: 10 - abs,
    filter: abs > 1 ? `blur(${(abs - 1) * 0.8}px)` : 'none',
    transition: 'all 0.65s cubic-bezier(0.16,1,0.3,1)',
    cursor: abs === 0 ? 'default' : 'pointer',
    pointerEvents: abs > 3 ? 'none' : 'auto',
    overflow: 'hidden',
    borderRadius: 14,
    background: T.surface,
    border: `1px solid ${abs === 0 ? 'rgba(255,255,255,0.11)' : T.border}`,
    display: 'flex',
    flexDirection: 'column',
  };
};

  return (
    <div style={{ marginBottom: 24, overflowX: 'hidden' }}>

      {/* Header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px 16px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)' }}>
          Trending News
        </span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.14)', fontFamily: "'DM Mono', monospace" }}>
          {articles.length}
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.16)' }}>
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </div>

      {!collapsed && (
        <div
          ref={stageRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{ position: 'relative' }}
        >
          {/* 3D stage */}
          <div style={{ perspective: 1100, position: 'relative', minHeight: cardH + 20, overflow: 'visible' }}>
            {/* Fade edges */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 100, background: `linear-gradient(to right, ${T.bg}, transparent)`, zIndex: 20, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 100, background: `linear-gradient(to left, ${T.bg}, transparent)`, zIndex: 20, pointerEvents: 'none' }} />

            <div style={{ position: 'relative', width: '100%', height: '100%', transformStyle: 'preserve-3d' }}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ ...getCardStyle(i - 1), background: T.surfaceHi }} />
                ))
                : articles.map((article, i) => {
                  const related  = findRelatedMarket(article);
                  const catColor = CATEGORY_COLORS[article.category] || '#3772ff';

                  return (
                    <div
                      key={article.id || i}
                      style={getCardStyle(i)}
                      onClick={() => {
                        const total = articles.length;
                        let offset = i - centerIdx;
                        if (offset > total / 2) offset -= total;
                        if (offset < -total / 2) offset += total;
                        if (Math.abs(offset) > 0) setCenterIdx(i);
                      }}
                    >
                      {/* Image */}
                      {article.image && !imgErrors[article.id || i] ? (
                        <div style={{ height: 220, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                          <img src={article.image} alt="" loading="lazy"
                            onError={() => setImgErrors(p => ({ ...p, [article.id || i]: true }))}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 55%)' }} />
                          {article.category && (
                            <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '2px 7px', borderRadius: 4, backdropFilter: 'blur(6px)' }}>
                              {article.category}
                            </span>
                          )}
                          <div style={{ position: 'absolute', bottom: 8, left: 10, right: 10, display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontFamily: "'DM Mono', monospace" }}>{srcName(article.url)}</span>
                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{ago(article.published)}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ height: 72, background: `${catColor}15`, borderBottom: `1px solid ${catColor}22`, display: 'flex', alignItems: 'center', padding: '0 14px', gap: 8, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{article.category || 'News'}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: "'DM Mono', monospace" }}>{ago(article.published)}</span>
                        </div>
                      )}

                      {/* Title */}
<div style={{ padding: '12px 14px' }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', letterSpacing: '-0.01em' }}>
                          {article.title}
                        </p>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
                        <a href={article.url} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ flex: 1, padding: '9px 0', textAlign: 'center', fontSize: 11, fontWeight: 500, color: T.textMuted, textDecoration: 'none', borderRight: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          Read article
                        </a>
                        {related ? (
                          <button onClick={e => { e.stopPropagation(); navigate(`/markets/${related.id}`); }}
                            style={{ flex: 1, padding: '9px 0', fontSize: 11, fontWeight: 600, color: catColor, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                            Trade this
                          </button>
                        ) : (
                          <button onClick={e => { e.stopPropagation(); navigate('/markets'); }}
                            style={{ flex: 1, padding: '9px 0', fontSize: 11, color: T.textDim, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            Browse markets
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>

          {/* Dot indicators */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, paddingTop: 14 }}>
            {articles.map((_, i) => (
              <div key={i} onClick={() => setCenterIdx(i)}
                style={{ width: i === centerIdx ? 18 : 5, height: 5, borderRadius: 3, background: i === centerIdx ? T.accent : 'rgba(255,255,255,0.1)', cursor: 'pointer', transition: 'all 0.3s ease' }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Sparkline = ({ data }) => {
  const { T } = useTheme();
  if (!data || data.length < 2) return null;
  const W = 56, H = 20;
  const vals = data.map(d => parseFloat(d.yes_percent));
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const trend = vals[vals.length - 1] - vals[vals.length - 2];
  const c = trend >= 0 ? T.yes : T.no;
  return (
    <svg width={W} height={H} style={{ overflow: 'visible', display: 'block' }}>
      <polyline points={pts} fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
    </svg>
  );
};

const ArcGauge = ({ pct, size = 48, color }) => {
  const { T } = useTheme();
  const c = color || T.yes;
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth="3" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth="3"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 1} textAnchor="middle" dominantBaseline="middle"
        fill={c} fontSize="10" fontWeight="700" fontFamily="'DM Mono', monospace">{pct}%</text>
    </svg>
  );
};

const LiveFlash = ({ flashes }) => {
  const { T } = useTheme();
  return (
    <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none', zIndex: 10 }}>
      {flashes.map(f => (
        <div key={f.id} style={{ fontSize: 11, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: f.isYes ? T.yes : T.no, animation: 'floatUp 2.5s ease forwards', whiteSpace: 'nowrap' }}>
          +{f.amount.toLocaleString()}
        </div>
      ))}
    </div>
  );
};

const fmtVol = (n) => {
  if (n >= 1e9) return `KES ${(n/1e9).toFixed(1)}B`;
  if (n >= 1e6) return `KES ${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `KES ${(n/1e3).toFixed(1)}K`;
  return `KES ${Math.round(n).toLocaleString()}`;
};

const sh = (w, h, r, T) => ({
  width: w, height: h, borderRadius: r, flexShrink: 0,
  background: `linear-gradient(90deg, ${T.surfaceHi} 25%, ${T.border} 50%, ${T.surfaceHi} 75%)`,
  backgroundSize: '400px 100%', animation: 'shimmer 1.4s infinite linear',
});

const MarketCardSkeleton = ({ isMulti = false }) => {
  const { T } = useTheme();
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 169 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={sh(40, 40, 8, T)} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={sh('92%', 13, 6, T)} />
            <div style={sh('68%', 13, 6, T)} />
            <div style={sh('38%', 10, 5, T)} />
          </div>
          <div style={sh(48, 48, 24, T)} />
        </div>
        {isMulti && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[70, 55, 45].map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={sh(6, 6, 3, T)} />
                <div style={{ ...sh(`${w}%`, 12, 5, T), flex: 1 }} />
                <div style={sh(32, 12, 5, T)} />
                <div style={sh(40, 24, 5, T)} />
              </div>
            ))}
          </div>
        )}
        {!isMulti && (
          <div style={{ display: 'flex', gap: 5 }}>
            <div style={sh('100%', 34, 6, T)} />
            <div style={sh('100%', 34, 6, T)} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 2 }}>
          <div style={sh(90, 11, 5, T)} />
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={sh(20, 20, 5, T)} />
            <div style={sh(80, 20, 5, T)} />
            <div style={sh(16, 16, 4, T)} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Comments = ({ marketId, token, navigate }) => {
  const { T } = useTheme();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/markets/${marketId}/comments`).then(r => setComments(r.data)).catch(() => {});
  }, [marketId]);

  const submit = async () => {
    if (!text.trim()) return token ? null : navigate('/login');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/markets/${marketId}/comments`, { content: text }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(p => [r.data, ...p]);
      setText('');
    } catch (_) {}
    setLoading(false);
  };

  const ago = ts => {
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s/60)}m`;
    if (s < 86400) return `${Math.floor(s/3600)}h`;
    return `${Math.floor(s/86400)}d`;
  };

  return (
    <div style={{ padding: '12px 14px', borderTop: `1px solid ${T.border}`, background: T.bg }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Discussion · {comments.length}</p>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Add your take..." maxLength={1000}
          style={{ flex: 1, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '7px 10px', color: T.text, fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
        <button onClick={submit} disabled={loading}
          style={{ background: text.trim() ? T.accent : T.surfaceHi, color: text.trim() ? '#fff' : T.textMuted, border: 'none', borderRadius: 6, padding: '7px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
          Post
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 140, overflowY: 'auto' }}>
        {comments.length === 0 && <p style={{ color: T.textDim, fontSize: 12, textAlign: 'center', padding: '6px 0' }}>No comments yet</p>}
        {comments.map(c => (
          <div key={c.id} style={{ padding: '7px 9px', background: T.surfaceHi, borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{c.username ? `@${c.username}` : c.full_name}</span>
              <span style={{ fontSize: 11, color: T.textDim }}>{ago(c.created_at)}</span>
            </div>
            <p style={{ fontSize: 12, color: T.text, lineHeight: 1.4 }}>{c.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const Share = ({ market }) => {
  const { T } = useTheme();
  const [copied, setCopied] = useState(false);
  const handle = async e => {
    e.stopPropagation();
    const url = `${window.location.origin}/markets/${market.id}`;
    if (navigator.share) { try { await navigator.share({ title: market.title, url }); } catch (_) {} }
    else { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {} }
  };
  return (
    <button onClick={handle} title="Share"
      style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 13, padding: '2px 4px', fontFamily: "'DM Mono', monospace", transition: 'color 0.1s' }}
      onMouseEnter={e => e.target.style.color = T.text}
      onMouseLeave={e => e.target.style.color = T.textMuted}>
      {copied ? '✓' : '↗'}
    </button>
  );
};

const StakeWarning = ({ est, amount, setAmount }) => {
  const { T } = useTheme();
  if (!est?.stake_warning || !(parseFloat(amount) > 0)) return null;
  const max = est.max_useful_stake;
  return (
    <div style={{ background:`${T.gold}12`, border:`1px solid ${T.gold}44`, borderRadius:6, padding:'8px 10px', marginBottom:8 }}>
      <p style={{ fontSize:11, fontWeight:700, color:T.gold, marginBottom:3 }}>⚠ Your profit is limited at this stake</p>
      <p style={{ fontSize:11, color:T.textMuted, lineHeight:1.5 }}>{est.stake_warning}</p>
      {max && parseFloat(amount) > max && (
        <button type="button" onClick={() => setAmount(String(max))}
          style={{ marginTop:6, background:'transparent', color:T.gold, border:`1px solid ${T.gold}55`, borderRadius:5, padding:'4px 8px', fontSize:11, fontWeight:700, cursor:'pointer' }}>
          Use KES {max.toLocaleString()} instead
        </button>
      )}
    </div>
  );
};

const BetPanel = ({ market, prediction, selectedOptionId, onClose, onSuccess, token, navigate }) => {
  const { T } = useTheme();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  

    const [est, setEst] = useState({ payout: 0, profit: 0 });

  useEffect(() => {
    const a = parseFloat(amount) || 0;
    if (a <= 0) { setEst({ payout: 0, profit: 0 }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/markets/${market.id}/estimate`, {
          params: { stake: a, side: prediction, option_id: selectedOptionId },
        });
        if (!cancelled) setEst({
  payout: r.data.payout,
  profit: parseFloat(r.data.profit.toFixed(2)),
  stake_warning: r.data.stake_warning,
  max_useful_stake: r.data.max_useful_stake,
});
      } catch (_) {}
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [amount, prediction, selectedOptionId, market.id, market.total_yes, market.total_no]);

  const { payout, profit } = est;

  const handleBet = async e => {
    e.preventDefault();
    setError('');
    if (!token) { navigate('/login'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/bets`,
        { market_id: market.id, prediction, amount: parseFloat(amount), option_id: selectedOptionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess(market, prediction, parseFloat(amount));
    } catch (err) { setError(err.response?.data?.message || 'Failed to place bet'); }
    finally { setLoading(false); }
  };

    
  const displayLabel = prediction?.startsWith('no_') ? `NO · ${prediction.slice(3)}` : prediction?.toUpperCase();
  const panelColor   = prediction?.startsWith('no_') ? T.no : prediction === 'yes' ? T.yes : T.accent;
  const QUICK = [50, 100, 500, 1000];

  return (
    <div onClick={e => e.stopPropagation()} style={{ background: T.bg, borderTop: `1px solid ${T.border}`, padding: '12px 14px', animation: 'slideDown 0.18s ease' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ padding: '7px 10px', background: T.surface, borderRadius: 5, flex: 1, marginRight: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: T.textMuted, fontSize: 11 }}>Est. return </span>
            <span style={{ color: profit >= 0 ? T.yes : T.no, fontWeight: 700, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
              KES {payout.toFixed(2)} ({profit >= 0 ? '+' : ''}KES {Math.abs(profit).toFixed(2)} · {parseFloat(amount) > 0 ? Math.round((profit / parseFloat(amount)) * 100) : 0}%)
            </span>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>
      {error && <div style={{ background: T.noDim, border: `1px solid ${T.no}22`, color: T.no, padding: '6px 10px', borderRadius: 5, marginBottom: 8, fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {QUICK.map(a => (
          <button key={a} onClick={() => setAmount(a.toString())}
            style={{ flex: 1, padding: '6px 0', borderRadius: 5, border: `1px solid ${amount === a.toString() ? panelColor : T.border}`, background: amount === a.toString() ? `${panelColor}18` : 'transparent', color: amount === a.toString() ? panelColor : T.textMuted, fontWeight: 600, cursor: 'pointer', fontSize: 11, fontFamily: "'DM Mono', monospace", transition: 'all 0.1s' }}>
            {a}
          </button>
        ))}
      </div>
            <StakeWarning est={est} amount={amount} setAmount={setAmount} />
      <form onSubmit={handleBet}>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (KES)" required min="1"
          style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '9px 12px', color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8, fontFamily: "'DM Mono', monospace" }} />
        <button type="submit" disabled={!amount || loading}
          style={{ width: '100%', background: !amount ? T.surfaceHi : panelColor, color: !amount ? T.textMuted : prediction === 'yes' ? '#0f1011' : '#fff', border: 'none', borderRadius: 6, padding: '10px', fontWeight: 700, cursor: !amount ? 'not-allowed' : 'pointer', fontSize: 13, transition: 'all 0.15s' }}>
          {loading ? 'Placing...' : `Confirm ${displayLabel}`}
        </button>
      </form>
    </div>
  );
};

const CountdownTimer = ({ closesAt }) => {
  const { T } = useTheme();
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calc = () => {
      const diff = new Date(closesAt) - Date.now();
      if (diff <= 0) { setTimeLeft(''); return; }
      if (diff > 30 * 60 * 1000) { setTimeLeft(''); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${m}:${s.toString().padStart(2, '0')}`);
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [closesAt]);

  if (!timeLeft) return null;

return (
    <span style={{
      fontSize: 9, fontWeight: 700, fontFamily: "'DM Mono', monospace",
      color: T.no, background: T.noDim, border: `1px solid ${T.no}55`,
      padding: '1px 6px', borderRadius: 4,
    }}>
      ⏱ {timeLeft}
    </span>
  );
};

const Card = ({ market, flashes, priceHistory, navigate, token, onBetSuccess, watched, onWatchChange, chamaContext }) => {
  const { T } = useTheme();
  const [activePred, setActivePred] = useState(null);
  const [activeOptId, setActiveOptId] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  const toggleWatch = async e => {
  e.stopPropagation();
  if (!token) { navigate('/login'); return; }
  try {
    const r = await axios.post(`${API}/api/markets/${market.id}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
    onWatchChange(market.id, r.data.bookmarked);
  } catch (_) {}
};

  const yes         = parseFloat(market.total_yes);
  const no          = parseFloat(market.total_no);
  const isMulti     = market.market_type === 'multiple';
  const total       = yes + no;
  const phEntry     = priceHistory[market.id];
  const history     = phEntry?.data || phEntry || [];
  const cardFlashes = flashes[market.id] || [];
  const isClosed    = market.status === 'closed';

  const getProbs = () => {
    if (isMulti && market.options?.length) {
      const rawRows = (phEntry?.data || []);
      return market.options.map((o, i) => {
        const optRows = rawRows.filter(r => String(r.option_id) === String(o.id));
        const latestPct = optRows.length ? parseFloat(optRows[optRows.length - 1].percent) : null;
        const totalPool = market.options.reduce((s, op) => s + parseFloat(op.total_amount || 0), 0);
        return {
          label: o.label, id: o.id, image_url: o.image_url || null,
          prob: latestPct !== null
            ? Math.round(latestPct)
            : totalPool > 0
              ? Math.round((parseFloat(o.total_amount || 0) / totalPool) * 100)
              : Math.round(100 / market.options.length),
          color: OPTION_COLORS[i % OPTION_COLORS.length],
        };
      });
    }

    const yesProb = market.yes_price
  ? Math.round(parseFloat(market.yes_price) * 100)
  : (() => {
      const b = 500;
      const yShares = parseFloat(market.yes_shares || 0);
      const nShares = parseFloat(market.no_shares  || 0);
      const scaled  = [yShares / b, nShares / b];
      const maxVal  = Math.max(...scaled);
      const exps    = scaled.map(s => Math.exp(s - maxVal));
      const sum     = exps.reduce((a, v) => a + v, 0);
      return Math.round((exps[0] / sum) * 100);
    })();
return [
  { label: 'Yes', prob: yesProb,       color: T.yes },
  { label: 'No',  prob: 100 - yesProb, color: T.no  },
];
  };

  const probs = getProbs();

  const handleClick = (e, label, optId = null) => {
    e.stopPropagation();
    if (isClosed) return;
    if (!token) { navigate('/login'); return; }
    if (activePred === label) { setActivePred(null); setActiveOptId(null); }
    else { setActivePred(label); setActiveOptId(optId); }
  };

  const handleSuccess = (m, p, a) => {
    setActivePred(null); setActiveOptId(null);
    onBetSuccess(m, p, a);
  };

  return (
    <div
      data-market-id={market.id}
      onClick={() => navigate(`/markets/${market.id}`, { state: chamaContext || undefined })}
      style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, position: 'relative', transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', cursor: 'pointer', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.2)' }}
      onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.15)'; e.currentTarget.style.boxShadow = `inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(55,114,255,0.2)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}>

      <LiveFlash flashes={cardFlashes} />

      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: isMulti ? 'unset' : 169 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: T.surfaceHi, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {market.image_url && !imgErr
              ? <img src={market.image_url} alt="" loading="lazy" onError={() => setImgErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{market.category?.slice(0,3)}</span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p onClick={() => navigate(`/markets/${market.id}`)}
              style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.4, cursor: 'pointer', marginBottom: 4, transition: 'color 0.1s' }}
              onMouseEnter={e => e.target.style.color = T.accent}
              onMouseLeave={e => e.target.style.color = T.text}>
              {market.title}
            </p>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
               <span style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{market.category}</span>
              {isClosed && <span style={{ fontSize: 9, color: T.gold, fontWeight: 700, background: 'rgba(243,169,74,0.12)', padding: '1px 5px', borderRadius: 3 }}>CLOSED</span>}
            </div>
          </div>
          {!isMulti && (!market.yes_label || market.yes_label === 'Yes') && (!market.no_label || market.no_label === 'No') && (
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <ArcGauge pct={probs[0].prob >= 50 ? probs[0].prob : probs[1].prob} color={probs[0].prob >= 50 ? T.yes : T.no} />
              {history.length > 1 && <Sparkline data={history} />}
            </div>
          )}
        </div>

        {isMulti && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: 60, overflowY: 'auto' }}>
            {probs.map(p => (
              <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                {p.image_url
                  ? <img src={p.image_url} alt="" loading="lazy" onError={e => e.target.style.display='none'} style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 6, height: 6, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                }
                <span style={{ color: T.text, fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p.label}</span>
                <span style={{ 
  color: T.textMuted, fontWeight: 600, fontSize: 13, 
  fontFamily: "'DM Mono', monospace", flexShrink: 0, minWidth: 36, textAlign: 'right' 
}}>
  {p.prob}%
</span>
{!isClosed && (
  <button onClick={e => handleClick(e, p.label, p.id)}
    style={{ 
      padding: '3px 9px', borderRadius: 5, border: 'none',
      background: activePred === p.label ? T.yes : T.yesDim,
      color: activePred === p.label ? '#0f1011' : T.yes,
      fontWeight: 700, cursor: 'pointer', fontSize: 11,
      whiteSpace: 'nowrap', transition: 'all 0.12s', flexShrink: 0,
    }}>
    Yes
  </button>
)}
              </div>
            ))}
          </div>
        )}

        {!isClosed && !isMulti && (
          <div style={{ display: 'flex', gap: 5 }}>
            <button onClick={e => handleClick(e, 'yes')}
              style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: `1px solid ${activePred === 'yes' ? T.yes : 'rgba(22,199,132,0.2)'}`, background: activePred === 'yes' ? T.yes : 'rgba(22,199,132,0.08)', color: activePred === 'yes' ? '#0f1011' : T.yes, fontWeight: 700, cursor: 'pointer', fontSize: 12, transition: 'all 0.15s',  boxShadow: activePred === 'yes' ? `0 0 16px ${T.yes}44` : 'none' }}>
              {market.yes_label || 'Yes'} · {probs[0].prob}%
            </button>
            <button onClick={e => handleClick(e, 'no')}
              style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: `1px solid ${activePred === 'no' ? T.no : 'rgba(234,57,67,0.2)'}`, background: activePred === 'no' ? T.no : 'rgba(234,57,67,0.08)', color: activePred === 'no' ? '#fff' : T.no, fontWeight: 700, cursor: 'pointer', fontSize: 12, transition: 'all 0.15s',  boxShadow: activePred === 'no' ? `0 0 16px ${T.no}44` : 'none' }}>
              {market.no_label || 'No'} · {probs[1].prob}%
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 2 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{total >= 10000 ? fmtVol(total) : '<10K'} Vol.</span>
            <span style={{ color: T.textDim, fontSize: 11 }}>{new Date(market.closes_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
            <CountdownTimer closesAt={market.closes_at} />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={toggleWatch} title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
              style={{ background: 'none', border: 'none', color: watched ? T.accent : T.textMuted, cursor: 'pointer', padding: '3px 4px', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={e => e.currentTarget.style.color = T.accent}
              onMouseLeave={e => e.currentTarget.style.color = watched ? T.accent : T.textMuted}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill={watched ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
            <button onClick={e => { e.stopPropagation(); setShowComments(s => !s); }}
              style={{ background: 'none', border: `1px solid ${showComments ? T.accent : T.border}`, borderRadius: 5, color: showComments ? T.accent : T.textMuted, cursor: 'pointer', fontSize: 11, padding: '3px 8px', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.1s', fontWeight: 500 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Comments
            </button>
            <Share market={market} />
          </div>
        </div>
      </div>

      {activePred && (
        token
          ? <BetPanel market={market} prediction={activePred} selectedOptionId={activeOptId}
              onClose={() => { setActivePred(null); setActiveOptId(null); }}
              onSuccess={handleSuccess} token={token} navigate={navigate} />
          : <div style={{ background: T.bg, borderTop: `1px solid ${T.border}`, padding: '12px 14px', textAlign: 'center' }}>
              <p style={{ color: T.text, fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Sign in to predict</p>
              <div style={{ display: 'flex', gap: 7, justifyContent: 'center' }}>
                <button onClick={() => navigate('/register')} style={{ background: T.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Create Account</button>
                <button onClick={() => navigate('/login')} style={{ background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 12 }}>Sign In</button>
              </div>
            </div>
      )}

      {showComments && <Comments marketId={market.id} token={token} navigate={navigate} />}
    </div>
  );
};

const SORTS = [
  { key: 'newest',      label: 'New' },
  { key: 'ending_soon', label: 'Ending Soon' },
  { key: 'volume',      label: 'Volume' },
  { key: 'active',      label: 'Active' },
];

export default function Markets() {
  const [markets, setMarkets]    = useState([]);
  const [loading, setLoading]    = useState(true);
  const [filter, setFilter]      = useState('all');
  const [sort, setSort]          = useState('newest');
  const [search, setSearch]      = useState('');
  const [flashes, setFlashes]    = useState({});
  const [priceHistory, setPH]    = useState({});
  const [successMsg, setSuccess] = useState('');
  const [page, setPage]          = useState(1);
  const PAGE_SIZE                = 80;
  const { token } = useAuth();
  const [bookmarked, setBookmarked] = useState(new Set());

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/markets/user/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setBookmarked(new Set(r.data.map(m => m.id))))
      .catch(() => {});
  }, [token]);

const [selectedCounty, setSelectedCounty] = useState('all');
 const [showCountyPopup, setShowCountyPopup] = useState(() => !localStorage.getItem('county_popup_seen'));
  const [showCountyPicker, setShowCountyPicker] = useState(false);
  const [countySearch, setCountySearch] = useState('');

  const COUNTY_TO_REGION = {
    'Nairobi': 'Nairobi',
    'Kiambu': 'Central', 'Murang\'a': 'Central', 'Kirinyaga': 'Central', 'Nyeri': 'Central', 'Nyandarua': 'Central',
    'Mombasa': 'Coast', 'Kwale': 'Coast', 'Kilifi': 'Coast', 'Tana River': 'Coast', 'Lamu': 'Coast', 'Taita-Taveta': 'Coast',
    'Machakos': 'Eastern', 'Makueni': 'Eastern', 'Kitui': 'Eastern', 'Embu': 'Eastern', 'Tharaka-Nithi': 'Eastern', 'Meru': 'Eastern', 'Marsabit': 'Eastern', 'Isiolo': 'Eastern',
    'Garissa': 'North Eastern', 'Wajir': 'North Eastern', 'Mandera': 'North Eastern',
    'Kisumu': 'Nyanza', 'Siaya': 'Nyanza', 'Homa Bay': 'Nyanza', 'Migori': 'Nyanza', 'Kisii': 'Nyanza', 'Nyamira': 'Nyanza',
    'Uasin Gishu': 'Rift Valley', 'Nakuru': 'Rift Valley', 'Baringo': 'Rift Valley', 'Laikipia': 'Rift Valley', 'Samburu': 'Rift Valley', 'Trans Nzoia': 'Rift Valley', 'Turkana': 'Rift Valley', 'West Pokot': 'Rift Valley', 'Elgeyo-Marakwet': 'Rift Valley', 'Nandi': 'Rift Valley', 'Kericho': 'Rift Valley', 'Bomet': 'Rift Valley', 'Narok': 'Rift Valley', 'Kajiado': 'Rift Valley',
    'Kakamega': 'Western', 'Vihiga': 'Western', 'Bungoma': 'Western', 'Busia': 'Western',
  };

  const KENYA_COUNTIES = [
    'Baringo','Bomet','Bungoma','Busia','Elgeyo-Marakwet','Embu','Garissa',
    'Homa Bay','Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi',
    'Kirinyaga','Kisii','Kisumu','Kitui','Kwale','Laikipia','Lamu','Machakos',
    'Makueni','Mandera','Marsabit','Meru','Migori','Mombasa','Murang\'a',
    'Nairobi','Nakuru','Nandi','Narok','Nyamira','Nyandarua','Nyeri',
    'Samburu','Siaya','Taita-Taveta','Tana River','Tharaka-Nithi','Trans Nzoia',
    'Turkana','Uasin Gishu','Vihiga','Wajir','West Pokot',
  ];

  const { dark, toggle: toggleTheme, T } = useTheme();
  const navigate = useNavigate();
  const sseRef  = useRef(null);
  const phCache = useRef({});
  const isMobile = useMobile();
const location = useLocation();
const chamaContext = location.state?.chamaMode ? location.state : null;

  useEffect(() => { fetchMarkets(); }, [sort]);

  useEffect(() => {
    if (!markets.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = parseInt(entry.target.dataset.marketId);
          if (id) fetchPH(id);
        }
      });
    }, { rootMargin: '150px' });
    const t = setTimeout(() => {
      document.querySelectorAll('[data-market-id]').forEach(el => observer.observe(el));
    }, 100);
    return () => { clearTimeout(t); observer.disconnect(); };
  }, [markets]);

  useEffect(() => {
    const connect = () => {
      if (!token) return;
      const url = `${API}/api/bets/live-feed?market_id=all&token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      sseRef.current = es;
      es.onmessage = e => {
        try {
          const d = JSON.parse(e.data);
          flash(d.market_id, d.amount, d.prediction !== 'no');
          refreshSingleMarket(d.market_id);
          delete phCache.current[d.market_id];
          fetchPH(d.market_id);
        } catch (_) {}
      };
      es.onerror = () => { es.close(); setTimeout(connect, 30000); };
    };
    connect();
    return () => sseRef.current?.close();
  }, [token]);

  const fetchMarkets = async () => {
    try {
      const r = await axios.get(`${API}/api/markets?sort=${sort}`);
      setMarkets(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const refreshSingleMarket = async (marketId) => {
    try {
      const r = await axios.get(`${API}/api/markets/${marketId}`);
      setMarkets(prev => prev.map(m => (m.id === marketId ? { ...m, ...r.data } : m)));
    } catch (_) {}
  };

  const fetchPH = async id => {
    const now = Date.now();
    if (phCache.current[id] && now - phCache.current[id] < 60000) return;
    try {
      const r = await axios.get(`${API}/api/markets/${id}/price-history`);
      phCache.current[id] = now;
      setPH(p => ({ ...p, [id]: r.data }));
    } catch (_) {}
  };

  const flash = (marketId, amount, isYes) => {
    const id = Date.now() + Math.random();
    setFlashes(f => ({ ...f, [marketId]: [...(f[marketId] || []).slice(-2), { id, amount, isYes }] }));
    setTimeout(() => setFlashes(f => ({ ...f, [marketId]: (f[marketId] || []).filter(x => x.id !== id) })), 2500);
  };

 const handleBetSuccess = (market, pred, amount) => {
    flash(market.id, amount, pred !== 'no');

    const isMultiMkt    = market.market_type === 'multiple';
    const wasFirstTrade = isMultiMkt
      ? !(market.options?.some(o => parseFloat(o.total_amount || 0) > 0))
      : (parseFloat(market.total_yes) + parseFloat(market.total_no)) === 0;

    setSuccess(
      wasFirstTrade
        ? `You're the first trader on this market! Placed KES ${amount.toLocaleString()} on ${pred.toUpperCase()}`
        : `Placed KES ${amount.toLocaleString()} on ${pred.toUpperCase()}`
    );

    refreshSingleMarket(market.id);
    delete phCache.current[market.id];
    fetchPH(market.id);
    setTimeout(() => setSuccess(''), 4000);
  };

  const categories = ['all', ...new Set(markets.map(m => m.category))];

  const locationFiltered = (() => {
    if (selectedCounty === 'all') return markets;
    const region = COUNTY_TO_REGION[selectedCounty];
    const byCounty  = markets.filter(m => m.location === selectedCounty);
    if (byCounty.length > 0) return [...byCounty, ...markets.filter(m => m.location === 'national')];
    const byRegion  = markets.filter(m => region && COUNTY_TO_REGION[m.location] === region);
    if (byRegion.length > 0) return [...byRegion, ...markets.filter(m => m.location === 'national')];
    return markets.filter(m => m.location === 'national');
  })();

  const filtered = locationFiltered
    .filter(m => filter === 'all' || m.category === filter)
    .filter(m => !search || m.title.toLowerCase().includes(search.toLowerCase()));


  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const cols = isMobile ? '1fr'
    : window.innerWidth < 700  ? 'repeat(2, 1fr)'
    : window.innerWidth < 1000 ? 'repeat(3, 1fr)'
    :                            'repeat(4, 1fr)';

  const SunIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
  const MoonIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );

  return (
    <div style={{ minHeight: '100vh', background: `radial-gradient(ellipse at 20% 0%, rgba(55,114,255,0.06) 0%, transparent 50%), radial-gradient(ellipse at 80% 0%, rgba(22,199,132,0.04) 0%, transparent 50%), ${T.bg}`, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text, paddingBottom: isMobile ? 72 : 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      <Helmet>
        <title>Mino Markets – Prediction Markets & Trading Platform</title>
        <meta name="description" content="Browse and trade prediction markets on real-world events, trends, and outcomes in real time." />
      </Helmet>
      <style>{`
        @keyframes fadeUp    { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes floatUp   { 0%{opacity:0;transform:translateY(4px)} 15%{opacity:1;transform:translateY(0)} 80%{opacity:1;transform:translateY(-10px)} 100%{opacity:0;transform:translateY(-18px)} }
        @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes shimmer   { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:3px; height:3px; }
        ::-webkit-scrollbar-thumb { background:${T.border}; border-radius:2px; }
        input:focus { border-color:${T.accent} !important; outline:none; }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; }
      `}</style>

      <nav style={{ background: `${T.bg}f0`, backdropFilter: 'blur(16px)', borderBottom: `1px solid ${T.border}`, height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100, gap: 16 }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="140" height="38" viewBox="0 0 160 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
            <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="18" fill={T.text} letterSpacing="-1">Mino Markets</text>
          </svg>
        </div>
        <div style={{ flex: 1, maxWidth: 480, position: 'relative' }}>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search markets..."
            style={{ width: '100%', background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 999, padding: '9px 18px 9px 38px', color: T.text, fontSize: 13, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s, box-shadow 0.2s' }}
            onFocus={e => { e.target.style.borderColor = T.accent; e.target.style.boxShadow = `0 0 0 3px ${T.accent}18`; }}
            onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          <button onClick={toggleTheme} style={{ background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7, padding: '7px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {dark ? <SunIcon /> : <MoonIcon />}
          </button>
          {!isMobile && (
            token ? (
              <button onClick={() => navigate('/dashboard')} style={{ background: T.surface, color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Dashboard</button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} style={{ background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Sign in</button>
                <button onClick={() => navigate('/register')} style={{ background: T.accent, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Sign up</button>
              </>
            )
          )}
        </div>
      </nav>

      <div style={{ maxWidth: 1440, margin: '0 auto', padding: isMobile ? '16px 12px' : '20px 20px', animation: 'fadeUp 0.3s ease', flex: 1, width: '100%' }}>
  {chamaContext && (
    <div style={{
      background: `${T.accent}14`, border: `1px solid ${T.accent}44`,
      borderRadius: 10, padding: '12px 18px', marginBottom: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7, background: T.accentDim,
          border: `1px solid ${T.accent}44`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 13,
        }}>👤👤👤</div>
        <div>
          <div style={{ color: T.accent, fontSize: 13, fontWeight: 600 }}>
            Trading as <strong>{chamaContext.chamaName}</strong>
          </div>
          <div style={{ color: T.textMuted, fontSize: 11 }}>
            Pool balance: KES {chamaContext.chamaBalance?.toLocaleString()} · Click a market to trade
          </div>
        </div>
      </div>
      <button onClick={() => navigate('/markets', { replace: true, state: {} })} style={{
        background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7,
        color: T.textMuted, fontSize: 12, padding: '5px 12px', cursor: 'pointer',
        fontFamily: 'inherit',
      }}>Exit chama mode</button>
    </div>
  )}
  <NewsPanel markets={markets} navigate={navigate} T={T} isMobile={isMobile} />

        <div style={{ display: 'flex', gap: 6, marginBottom: 8, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => { setFilter(cat); setPage(1); }} style={{
              padding: '6px 14px', background: filter === cat ? T.surfaceHi : 'transparent',
              border: `1px solid ${filter === cat ? T.borderHi : 'transparent'}`,
              borderRadius: 20, color: filter === cat ? T.text : T.textMuted,
              fontWeight: filter === cat ? 600 : 400, cursor: 'pointer', fontSize: 13,
              textTransform: 'capitalize', whiteSpace: 'nowrap', transition: 'all 0.12s',
            }}>{cat === 'all' ? 'All Markets' : cat}</button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, overflowX: 'auto', scrollbarWidth: 'none' }}>
          <span style={{ fontSize: 11, color: T.textDim, fontWeight: 500, flexShrink: 0 }}>Sort by</span>
          {SORTS.map(s => (
            <button key={s.key} onClick={() => { setSort(s.key); setPage(1); }} style={{
              padding: '5px 12px', background: sort === s.key ? T.accent : 'transparent',
              border: `1px solid ${sort === s.key ? T.accent : T.border}`,
              color: sort === s.key ? '#fff' : T.textMuted,
              borderRadius: 20, fontWeight: sort === s.key ? 600 : 400,
              cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', transition: 'all 0.12s',
            }}>{s.label}</button>
          ))}
        </div>

          {/* County filter bar */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
          <button
            onClick={() => setShowCountyPicker(true)}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 14px', borderRadius:20, border:`1px solid ${selectedCounty!=='all'?T.accent:T.border}`, background:selectedCounty!=='all'?T.accentDim:'transparent', color:selectedCounty!=='all'?T.accent:T.textMuted, cursor:'pointer', fontSize:13, fontWeight:selectedCounty!=='all'?600:500, transition:'all 0.2s', flexShrink:0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {selectedCounty === 'all' ? 'All Kenya' : selectedCounty}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {selectedCounty !== 'all' && (
            <button onClick={() => setSelectedCounty('all')} style={{ background:'none', border:'none', color:T.textDim, cursor:'pointer', fontSize:12, padding:'4px 8px', borderRadius:6, transition:'color 0.15s' }} onMouseEnter={e=>e.currentTarget.style.color=T.textMuted} onMouseLeave={e=>e.currentTarget.style.color=T.textDim}>
              Clear
            </button>
          )}
          {selectedCounty !== 'all' && (() => {
            const region = COUNTY_TO_REGION[selectedCounty];
            const byCounty = markets.filter(m => m.location === selectedCounty);
            const byRegion = markets.filter(m => region && COUNTY_TO_REGION[m.location] === region);
            if (byCounty.length > 0) return <span style={{ fontSize:12, color:T.textDim }}>{byCounty.length} market{byCounty.length!==1?'s':''} in {selectedCounty}</span>;
            if (byRegion.length > 0) return <span style={{ fontSize:12, color:T.textDim }}>No markets in {selectedCounty} — showing {region} region</span>;
            return <span style={{ fontSize:12, color:T.textDim }}>No markets in {selectedCounty} — showing national</span>;
          })()}
        </div>

        {/* County picker modal */}
        {showCountyPicker && (
          <div
            onClick={() => setShowCountyPicker(false)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(6px)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16, animation:'fadeIn 0.2s ease' }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16, width:'100%', maxWidth:480, maxHeight:'80vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.5)', animation:'scaleIn 0.25s cubic-bezier(0.16,1,0.3,1)' }}>
              <style>{`
                @keyframes fadeIn { from{opacity:0} to{opacity:1} }
                @keyframes scaleIn { from{opacity:0;transform:scale(0.95) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }
              `}</style>
              {/* Header */}
              <div style={{ padding:'20px 20px 16px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                <div>
                  <p style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:3 }}>Select County</p>
                  <p style={{ fontSize:12, color:T.textMuted }}>Filter markets by your county or region</p>
                </div>
                <button onClick={() => setShowCountyPicker(false)} style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:T.textMuted, fontSize:16, flexShrink:0, transition:'all 0.15s' }} onMouseEnter={e=>{e.currentTarget.style.background=T.borderHi;e.currentTarget.style.color=T.text}} onMouseLeave={e=>{e.currentTarget.style.background=T.surfaceHi;e.currentTarget.style.color=T.textMuted}}>×</button>
              </div>
              {/* Search */}
              <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`, flexShrink:0, position:'relative' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" strokeLinecap="round" style={{ position:'absolute', left:32, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  autoFocus
                  value={countySearch}
                  onChange={e => setCountySearch(e.target.value)}
                  placeholder="Search county..."
                  style={{ width:'100%', background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'9px 12px 9px 34px', color:T.text, fontSize:13, outline:'none', fontFamily:'inherit', boxSizing:'border-box', transition:'border-color 0.15s' }}
                  onFocus={e=>e.target.style.borderColor=T.accent}
                  onBlur={e=>e.target.style.borderColor=T.border}
                />
              </div>
              {/* List */}
              <div style={{ overflowY:'auto', flex:1, padding:'8px 0' }}>
                {/* All Kenya option */}
                {!countySearch && (
                  <div
                    onClick={() => { setSelectedCounty('all'); setShowCountyPicker(false); setCountySearch(''); }}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 20px', cursor:'pointer', transition:'background 0.1s', background:selectedCounty==='all'?T.accentDim:'transparent' }}
                    onMouseEnter={e=>e.currentTarget.style.background=selectedCounty==='all'?T.accentDim:T.surfaceHi}
                    onMouseLeave={e=>e.currentTarget.style.background=selectedCounty==='all'?T.accentDim:'transparent'}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:selectedCounty==='all'?T.accent:T.surfaceHi, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.15s' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={selectedCounty==='all'?'#fff':T.textMuted} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      </div>
                      <div>
                        <p style={{ fontSize:13, fontWeight:600, color:selectedCounty==='all'?T.accent:T.text }}>All Kenya</p>
                        <p style={{ fontSize:11, color:T.textDim }}>{markets.filter(m=>m.location==='national').length + markets.filter(m=>m.location!=='national').length} markets</p>
                      </div>
                    </div>
                    {selectedCounty==='all' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                )}
                {/* Counties */}
                {KENYA_COUNTIES.filter(c => !countySearch || c.toLowerCase().includes(countySearch.toLowerCase())).map(county => {
                  const count = markets.filter(m => m.location === county).length;
                  const region = COUNTY_TO_REGION[county];
                  const isSelected = selectedCounty === county;
                  return (
                    <div key={county}
                      onClick={() => { setSelectedCounty(county); setShowCountyPicker(false); setCountySearch(''); }}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 20px', cursor:'pointer', transition:'background 0.1s', background:isSelected?T.accentDim:'transparent' }}
                      onMouseEnter={e=>e.currentTarget.style.background=isSelected?T.accentDim:T.surfaceHi}
                      onMouseLeave={e=>e.currentTarget.style.background=isSelected?T.accentDim:'transparent'}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:8, background:isSelected?T.accent:T.surfaceHi, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.15s' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isSelected?'#fff':T.textMuted} strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        </div>
                        <div>
                          <p style={{ fontSize:13, fontWeight:isSelected?600:400, color:isSelected?T.accent:T.text }}>{county}</p>
                          <p style={{ fontSize:11, color:T.textDim }}>{region} · {count > 0 ? `${count} market${count!==1?'s':''}` : 'no local markets'}</p>
                        </div>
                      </div>
                      {isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

         {/* One-time session popup — full overlay modal */}
        {showCountyPopup && (
          <div
            onClick={() => { setShowCountyPopup(false); localStorage.setItem('county_popup_seen','1'); }}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(8px)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding:20, animation:'fadeIn 0.25s ease' }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:18, width:'100%', maxWidth:400, padding:28, boxShadow:'0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)', animation:'scaleIn 0.3s cubic-bezier(0.16,1,0.3,1)', position:'relative' }}>
              {/* Close */}
              <button
                onClick={() => { setShowCountyPopup(false); localStorage.setItem('county_popup_seen','1'); }}
                style={{ position:'absolute', top:16, right:16, background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:T.textMuted, fontSize:15, transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.background=T.borderHi;e.currentTarget.style.color=T.text}}
                onMouseLeave={e=>{e.currentTarget.style.background=T.surfaceHi;e.currentTarget.style.color=T.textMuted}}>
                ×
              </button>
              {/* Icon */}
              <div style={{ width:48, height:48, borderRadius:12, background:T.accentDim, border:`1px solid ${T.accent}22`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:18 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <p style={{ fontSize:17, fontWeight:700, color:T.text, marginBottom:10, lineHeight:1.3 }}>Markets near you</p>
              <p style={{ fontSize:13, color:T.textMuted, lineHeight:1.7, marginBottom:24 }}>
                You can now filter markets by county. Pick your county to see what's being traded near you, or browse all of Kenya.
              </p>
              {/* Divider */}
              <div style={{ height:1, background:T.border, marginBottom:20 }}/>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <button
                  onClick={() => { setShowCountyPopup(false); localStorage.setItem('county_popup_seen','1'); setShowCountyPicker(true); }}
                  style={{ width:'100%', background:T.accent, color:'#fff', border:'none', borderRadius:10, padding:'12px 0', fontWeight:600, cursor:'pointer', fontSize:14, transition:'opacity 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.opacity='0.85'}
                  onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                  Select my county
                </button>
                <button
                  onClick={() => { setShowCountyPopup(false); localStorage.setItem('county_popup_seen','1'); }}
                  style={{ width:'100%', background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:10, padding:'12px 0', fontWeight:500, cursor:'pointer', fontSize:14, transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderHi;e.currentTarget.style.color=T.text}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textMuted}}>
                  Browse all of Kenya
                </button>
              </div>
            </div>
          </div>
        )}

        {successMsg && (
          <div style={{ background: T.yesDim, border: `1px solid ${T.yes}33`, color: T.yes, padding: '9px 14px', borderRadius: 7, marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
            {successMsg}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10 }}>
            {Array.from({ length: 8 }).map((_, i) => <MarketCardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: T.textDim }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>—</p>
            <p style={{ fontSize: 14 }}>No markets found</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10 }}>
              {paginated.map(m => (
                <Card key={m.id} market={m} flashes={flashes} priceHistory={priceHistory}
  navigate={navigate} token={token} onBetSuccess={handleBetSuccess}
  watched={bookmarked.has(m.id)}
  chamaContext={chamaContext}
  onWatchChange={(id, val) => setBookmarked(prev => {
    const s = new Set(prev);
    val ? s.add(id) : s.delete(id);
    return s;
  })} />
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 28, marginBottom: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={page === 1}
                  style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'transparent', color: page === 1 ? T.textDim : T.textMuted, cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.12s' }}>
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
                  .reduce((acc, n, idx, arr) => {
                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push('...');
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === '...' ? (
                      <span key={`ellipsis-${i}`} style={{ color: T.textDim, fontSize: 13, padding: '0 4px' }}>…</span>
                    ) : (
                      <button key={n}
                        onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{ width: 36, height: 36, borderRadius: 7, border: `1px solid ${n === page ? T.accent : T.border}`, background: n === page ? T.accent : 'transparent', color: n === page ? '#fff' : T.textMuted, cursor: 'pointer', fontSize: 13, fontWeight: n === page ? 700 : 400, transition: 'all 0.12s' }}>
                        {n}
                      </button>
                    )
                  )
                }
                <button
                  onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={page === totalPages}
                  style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'transparent', color: page === totalPages ? T.textDim : T.textMuted, cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.12s' }}>
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 32, background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}>

        {/* Subtle top shimmer line */}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent 0%, rgba(55,114,255,0.4) 30%, rgba(22,199,132,0.4) 70%, transparent 100%)' }} />

        {/* Main columns */}
        <div style={{ padding: '40px 32px 32px', display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 32 }}>

          {/* Brand column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }} onClick={() => navigate('/markets')}>
              <svg width="32" height="32" viewBox="0 0 38 38" fill="none">
                <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
                <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
                <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.5px', color: T.text }}>Mino Markets</span>
            </div>
            <p style={{ fontSize: 13, color: T.textDim, lineHeight: 1.7, maxWidth: 200, marginBottom: 20 }}>
              Kenya's prediction markets platform. Trade on real-world events with real money.
            </p>

            {/* Socials */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <a href="https://x.com/MinoMarkets254" target="_blank" rel="noopener noreferrer"
                style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill={T.textMuted}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.735-8.849L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="https://instagram.com/minomarkets_254" target="_blank" rel="noopener noreferrer"
                style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </a>
              <a href="https://tiktok.com/@minomarkets_254" target="_blank" rel="noopener noreferrer"
                style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill={T.textMuted}>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
                </svg>
              </a>
            </div>

            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', color: T.textDim }}>
              🇰🇪 Made in Kenya
            </span>

            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 700, marginBottom: 5, letterSpacing: '0.01em' }}>
                Building Africa's prediction markets
              </p>
              <p style={{ fontSize: 12, color: T.textDim, lineHeight: 1.7, marginBottom: 12 }}>
                We're early-stage and open to partners who believe in the future of African fintech.
              </p>
              <span
                onClick={() => navigate('/investors')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: T.textMuted, cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.gap = '8px'; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.gap = '5px'; }}>
                Partner with us
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
            </div>
          </div>

          {/* Platform */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 14 }}>Platform</p>
            {[
              ['Markets',      '/markets'],
              ['Leaderboard',  '/leaderboard'],
              ['How It Works', '/how-it-works'],
              ['FAQ',          '/responsible-gambling'],
            ].map(([label, path]) => (
              <span key={path} onClick={() => navigate(path)}
                style={{ display: 'block', color: T.textMuted, fontSize: 13, cursor: 'pointer', lineHeight: 2.2, transition: 'color 0.12s' }}
                onMouseEnter={e => e.target.style.color = T.text}
                onMouseLeave={e => e.target.style.color = T.textMuted}>{label}</span>
            ))}
          </div>

          {/* Account */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 14 }}>Account</p>
            {[['Sign Up', '/register'], ['Sign In', '/login'], ['Dashboard', '/dashboard'], ['Deposit', '/deposit']].map(([label, path]) => (
              <span key={label} onClick={() => navigate(path)}
                style={{ display: 'block', color: T.textMuted, fontSize: 13, cursor: 'pointer', lineHeight: 2.2, transition: 'color 0.12s' }}
                onMouseEnter={e => e.target.style.color = T.text}
                onMouseLeave={e => e.target.style.color = T.textMuted}>{label}</span>
            ))}
          </div>

          {/* Legal */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 14 }}>Legal</p>
            {[['Terms of Service', '/terms'], ['Privacy Policy', '/privacy'], ['Responsible Trading', '/responsible-gambling']].map(([label, path]) => (
              <span key={path} onClick={() => navigate(path)}
                style={{ display: 'block', color: T.textMuted, fontSize: 13, cursor: 'pointer', lineHeight: 2.2, transition: 'color 0.12s' }}
                onMouseEnter={e => e.target.style.color = T.text}
                onMouseLeave={e => e.target.style.color = T.textMuted}>{label}</span>
            ))}
          </div>

          {/* Company */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textDim, marginBottom: 14 }}>Company</p>
            {[
              ['About',     '/about'],
              ['Investors', '/investors'],
              ['Blog',      '/blog'],
              ['Contact',   '/investors'],
            ].map(([label, path]) => (
              <span key={label}
                onClick={() => path.startsWith('mailto') ? window.location.href = path : navigate(path)}
                style={{ display: 'block', color: T.textMuted, fontSize: 13, cursor: 'pointer', lineHeight: 2.2, transition: 'color 0.12s' }}
                onMouseEnter={e => e.target.style.color = T.text}
                onMouseLeave={e => e.target.style.color = T.textMuted}>{label}</span>
            ))}
          </div>

        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(16px)' }}>
          <p style={{ fontSize: 12, color: T.textDim }}>© 2026 Mino · DAFCOM ENTERPRISES · BN/2009/43477</p>
          <p style={{ fontSize: 11, color: T.textDim, maxWidth: 420, textAlign: 'right', lineHeight: 1.5 }}>
            Trading involves risk. Only trade what you can afford to lose. 18+ only.
          </p>
        </div>
      </footer>

      <BottomNav />
    </div>
  );
}